import { computed, getCurrentInstance } from '@vue/composition-api'
import { isCustomerProjectRoute, isCustomerAccountRoute } from '@/navigation/customerContext'
import { allows as membershipAllows } from '@/utils/membership'

export default function reactiveNavigation() {
  const { setupContext } = getCurrentInstance()
  const { proxy } = getCurrentInstance()
  const { root } = setupContext
  const route = computed(() => proxy.$route.name)
  const store = root.$store
  const readStoredUserData = () => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}')
    } catch (error) {
      return {}
    }
  }
  const userData = computed(() => {
    const currentUser = store.getters['auth/currentUser'] || {}
    const source = Object.keys(currentUser).length ? currentUser : readStoredUserData()
    return {
      ...source,
      scoped_roles: Array.isArray(source.scoped_roles) ? source.scoped_roles : [],
    }
  })
  const project = computed(() => store.getters['projects/project'])
  const pendingMaterialSuggestions = computed(() => store.getters['materialSuggestions/pendingCount'])
  const supplier = computed(() => store.getters['suppliers/supplier'])
  const customerId = computed(() => store.getters['customers/selectedCustomerResourceId'])
  const customerScopedRole = computed(() => {
    const scopedRoles = Array.isArray(userData.value.scoped_roles) ? userData.value.scoped_roles : []
    return scopedRoles.find(role => role.role_name === 'customer' && role.role_resource_type === 'Customer')
      || scopedRoles.find(role => role.role_resource_type === 'Customer')
      || scopedRoles[0]
      || null
  })
  const resolvedCustomerId = computed(() => customerId.value || customerScopedRole.value?.role_resource_id || null)
  const activeWalletCustomerId = computed(() => {
    if (route.value === 'WalletForCustomer') {
      return proxy.$route.params.customerId || resolvedCustomerId.value
    }
    return resolvedCustomerId.value
  })
  // const customerId = computed(() => store.state.customers.selectedCustomerResourceId)

  const compact = arr => arr.filter(Boolean)

  // Lo que el plan del cliente incluye. Lo que no incluye no se esconde: se marca con
  // etiqueta PRO y lleva a contratar, que es donde se decide la compra.
  const membership = computed(() => store.getters['memberships/currentMembership'])
  const planAllows = key => membershipAllows(membership.value, key)
  // Lo que el plan no incluye conserva su ruta: el cliente entra a la pantalla y ahi se
  // le explica y se le manda a contratar. Cambiarle la ruta aqui lo dejaba sin poder ver
  // el modulo y ademas marcaba dos items del menu como activos a la vez.
  const upsell = (featureKey, item) => (planAllows(featureKey) ? item : {
    ...item,
    tag: 'PRO',
    tagVariant: 'light-warning',
  })

  const adminNavigation = computed(() => [
    {
      title: 'providers',
      route: 'suppliers',
      icon: 'TruckIcon',
    },
    {
      title: 'supplierTags',
      route: 'supplier-tags',
      icon: 'TagIcon',
    },
    {
      title: 'ProductCatalogs',
      icon: 'PackageIcon',
      children: [
        {
          title: 'products',
          route: 'productsCatalogs',
          icon: 'BoxIcon',
        },
        {
          title: 'categories',
          route: 'categories',
          icon: 'TagIcon',
        },
        {
          title: 'productTags',
          route: 'product-tags',
          icon: 'TagIcon',
        },
        {
          title: 'materialSuggestions',
          route: 'materialSuggestions',
          icon: 'InboxIcon',
          tag: pendingMaterialSuggestions.value || null,
          tagVariant: 'light-warning',
        },
      ],
    },
    {
      title: 'customers',
      route: 'customers',
      icon: 'UserIcon',
    },
    {
      title: 'users',
      route: 'users',
      icon: 'UserCheckIcon',
    },
    {
      title: 'quotes',
      icon: 'FilePlusIcon',
      children: [
        {
          title: 'allQuotes',
          route: 'allQuotes',
          icon: 'ListIcon',
        },
        {
          title: 'purchaseOrders',
          route: 'payments',
          icon: 'ShoppingCartIcon',
        },
        {
          title: 'overduePayments',
          route: 'overduePayments',
          icon: 'AlertOctagonIcon',
        },
      ],
    },
    {
      title: 'invoices',
      icon: 'FileTextIcon',
      children: [
        {
          title: 'customer',
          route: 'customerInvoices',
          icon: 'UserIcon',
        },
        {
          title: 'suppliers',
          route: 'supplierInvoices',
          icon: 'TruckIcon',
        },
      ],
    },
    {
      title: 'finance',
      icon: 'BarChart2Icon',
      children: [
        {
          title: 'accountsPayable',
          route: 'accountsPayable',
          icon: 'ArrowDownCircleIcon',
        },
        {
          title: 'accountsReceivable',
          route: 'accountsReceivable',
          icon: 'ArrowUpCircleIcon',
        },
        {
          title: 'balance',
          route: 'balance',
          icon: 'ActivityIcon',
        },
      ],
    },
    {
      title: 'WalletSummaryAdmin',
      route: 'WalletSummaryAdmin',
      icon: 'PocketIcon',
    },
    {
      title: 'Warehouse',
      route: 'Warehouse',
      icon: 'ArchiveIcon',
    },
    {
      title: 'suppliesExplosionYoc',
      route: 'suppliesExplosionYoc',
      icon: 'PieChartIcon',
    },
    {
      title: 'reports',
      icon: 'BarChart2Icon',
      children: [
        {
          title: 'weekly',
          route: 'adminReportsWeekly',
          icon: 'CalendarIcon',
        },
        {
          title: 'weeklyCustomers',
          route: 'adminReportsWeeklyCustomers',
          icon: 'UsersIcon',
        },
        {
          title: 'monthly',
          route: 'adminReportsMonthly',
          icon: 'BarChartIcon',
        },
        {
          title: 'monthlyCustomers',
          route: 'adminReportsMonthlyCustomers',
          icon: 'UsersIcon',
        },
      ],
    },
    {
      title: 'notifications',
      route: 'notificationSettings',
      icon: 'BellIcon',
    },
    {
      title: 'settings',
      icon: 'SettingsIcon',
      children: [
        {
          title: 'generalSettings',
          route: 'settings',
          icon: 'SlidersIcon',
        },
        {
          title: 'loginSlides',
          route: 'loginSlidesSettings',
          icon: 'ImageIcon',
        },
        {
          title: 'legalDocuments',
          route: 'legalDocumentsSettings',
          icon: 'FileTextIcon',
        },
      ],
    },
    {
      title: 'memberships',
      icon: 'AwardIcon',
      children: [
        {
          title: 'membershipPlans',
          route: 'membershipPlansSettings',
          icon: 'SlidersIcon',
        },
        {
          title: 'membershipRequests',
          route: 'membershipRequests',
          icon: 'InboxIcon',
        },
      ],
    },
    {
      title: 'referrals',
      route: 'referralsGlobal',
      icon: 'GiftIcon',
    },
    {
      title: 'myReferrals',
      route: 'referrals',
      icon: 'UserIcon',
    },
    {
      title: 'raiseAppTicket',
      route: 'ticketNew',
      icon: 'LifeBuoyIcon',
      navItemClass: 'ticket-raise-cta-item',
      navLinkClass: 'ticket-raise-cta-link',
      tag: 'APP',
      tagVariant: 'light-primary',
    },
  ])

  const adminNavigationLikeCustomer = computed(() => {
    const nav = [
      {
        title: 'customers',
        route: 'customers',
        icon: 'ArrowLeftIcon',
      },
      {
        title: 'projects',
        custom: customerId.value,
        route: {
          name: 'projects',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'FolderIcon',
      },
      {
        title: 'allMyQuotes',
        route: {
          name: 'allMyQuotes',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'FileTextIcon',
      },
      {
        title: 'allMyExpenses',
        route: {
          name: 'allMyExpenses',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'TrendingDownIcon',
      },
      {
        title: 'users',
        route: {
          name: 'members',
          params: {
            userId: customerId.value,
          },
        },
        icon: 'UsersIcon',
      },
      {
        title: 'billing',
        custom: customerId.value,
        route: {
          name: 'billing',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'CreditCardIcon',
      },
      {
        title: 'referrals',
        custom: customerId.value,
        route: {
          name: 'referrals',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'GiftIcon',
      },
      {
        title: 'WalletForCustomer',
        custom: customerId.value,
        route: {
          name: 'WalletForCustomer',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'PocketIcon',
      },
    ]
    return nav
  })

  const adminNavigationLikeCustomerProject = computed(() => {
    const nav = [
      {
        title: 'customers',
        route: 'customers',
        icon: 'ArrowLeftIcon',
      },
      {
        title: 'projects',
        custom: customerId.value,
        route: {
          name: 'projects',
          params: {
            customerId: customerId.value,
          },
        },
        icon: 'ArrowLeftIcon',
      },
      {
        title: 'quotes',
        route: {
          name: 'quotes',
          params: {
            projectId: project.value.id,
          },
        },
        icon: 'FileTextIcon',
      },
      {
        title: 'approvedQuotes',
        route: {
          name: 'approvedQuotes',
          params: {
            projectId: project.value.id,
          },
        },
        icon: 'CheckCircleIcon',
      },
      // {
      //   title: 'expenses',
      //   icon: 'SendIcon',
      //   children: [
      //     {
      //       title: 'all',
      //       route: {
      //         name: 'allExpenses',
      //         params: {
      //           projectId: project.value.id,
      //         },
      //       },
      //     },
      //     {
      //       title: 'byProduct',
      //       route: {
      //         name: 'expenses',
      //         params: {
      //           projectId: project.value.id,
      //         },
      //       },
      //     },
      //   ],
      // },
      {
        title: 'groupedExpenses',
        route: {
          name: 'groupedExpenses',
          params: {
            projectId: project.value.id,
          },
        },
        icon: 'PieChartIcon',
      },
      {
        title: 'management',
        icon: 'BriefcaseIcon',
        children: [
          {
            title: 'dashboard',
            route: {
              name: 'reports',
              params: {
                projectId: project.value.id,
              },
            },
            icon: 'BarChart2Icon',
          },
          {
            title: 'incomes',
            route: {
              name: 'incomes',
              params: {
                projectId: project.value.id,
              },
            },
            icon: 'TrendingUpIcon',
          },
          {
            title: 'expenses',
            route: {
              name: 'allExpenses',
              params: {
                projectId: project.value.id,
              },
            },
            icon: 'TrendingDownIcon',
          },
          {
            title: 'budget',
            route: {
              name: 'budget',
              params: {
                projectId: project.value.id,
              },
            },
            icon: 'BriefcaseIcon',
          },
        ],
      },
    ]
    if (!root.$helpers.validZone(project.value.address)) {
      const index = nav.findIndex(i => i.title === 'quotes')
      if (index > 0) {
        nav.splice(index, 1)
      }
    }
    return nav
  })

  const supplierAdminNavigation = computed(() => [
    {
      title: 'branches',
      route: {
        name: 'branches',
        params: {
          id: userData.value.scoped_roles[0]?.role_resource_id,
        },
      },
      icon: 'HomeIcon',
    },
    {
      title: 'products',
      icon: 'PackageIcon',
      route: {
        name: 'supplierProductsCatalogs',
        params: {
          type: 'proveedor',
          id: userData.value.scoped_roles[0]?.role_resource_id,
        },
      },
    },
    {
      title: 'accountsReceivable',
      icon: 'DollarSignIcon',
      route: { name: 'supplierReceivables' },
    },
  ])

  const auctioneerNavigation = computed(() => [
    {
      title: 'biddings',
      route: {
        name: 'supplierQuotes',
        params: {
          id: userData.value.scoped_roles[0]?.role_resource_id,
        },
      },
      icon: 'GavelIcon',
    },
    {
      title: 'sells',
      route: {
        name: 'supplierSells',
        params: {
          id: userData.value.scoped_roles[0]?.role_resource_id,
        },
      },
      icon: 'ShoppingBagIcon',
    },
    {
      title: 'settings',
      route: {
        name: 'branch',
        params: {
          branchId: userData.value.scoped_roles[0]?.role_resource_id,
          supplierId: userData.value.scoped_roles[0]?.role_resource_scoped_id,
        },
      },
      icon: 'SettingsIcon',
    },
  ])

  const clientNavigation = computed(() => compact([
    {
      title: 'myProjects',
      route: {
        name: 'projects',
        params: { customerId: resolvedCustomerId.value },
      },
      icon: 'HomeIcon',
    },
    {
      title: 'allMyQuotes',
      route: {
        name: 'allMyQuotes',
        params: { customerId: resolvedCustomerId.value },
      },
      icon: 'FileTextIcon',
    },
    upsell('purchase_management', {
      title: 'allMyExpenses',
      route: {
        name: 'allMyExpenses',
        params: { customerId: resolvedCustomerId.value },
      },
      icon: 'TrendingDownIcon',
    }),
    {
      title: 'accountStatement',
      route: { name: 'accountsReceivable' },
      icon: 'ClipboardIcon',
    },
    {
      title: 'users',
      route: {
        name: 'members',
        params: { userId: resolvedCustomerId.value },
      },
      icon: 'UsersIcon',
    },
    {
      title: 'billing',
      route: {
        name: 'billing',
        params: { customerId: resolvedCustomerId.value },
      },
      icon: 'CreditCardIcon',
    },
    {
      title: 'referrals',
      custom: resolvedCustomerId.value,
      route: {
        name: 'referrals',
        params: { customerId: resolvedCustomerId.value },
      },
      icon: 'GiftIcon',
    },
    {
      title: 'WalletForCustomer',
      custom: activeWalletCustomerId.value,
      route: {
        name: 'WalletForCustomer',
        params: { customerId: activeWalletCustomerId.value },
      },
      icon: 'PocketIcon',
    },
    {
      title: 'membership',
      route: { name: 'membership' },
      icon: 'AwardIcon',
    },
    {
      title: 'editAccount',
      route: {
        name: 'editCustomer',
        params: { customerId: userData.value.id },
      },
      icon: 'SettingsIcon',
    },
  ]))

  const clientProjectNavigation = computed(() => {
    if (project.value.id && project.value.id !== null) {
      const nav = [
        {
          title: 'myProjects',
          route: {
            name: 'projects',
            params: {
              customerId: resolvedCustomerId.value,
            },
          },
          icon: 'ArrowLeftIcon',
        },
        // {
        //   title: 'dashboard',
        //   route: {
        //     name: 'reports',
        //     params: {
        //       projectId: project.value.id,
        //     },
        //   },
        //   icon: 'ClipboardIcon',
        // },
        {
          title: 'quotes',
          route: {
            name: 'quotes',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'FileTextIcon',
        },
        {
          title: 'approvedQuotes',
          route: {
            name: 'approvedQuotes',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'CheckCircleIcon',
        },
        // {
        //   title: 'incomes',
        //   route: {
        //     name: 'incomes',
        //     params: {
        //       projectId: project.value.id,
        //     },
        //   },
        //   icon: 'CheckCircleIcon',
        // },
        // {
        //   title: 'expenses',
        //   route: {
        //     name: 'allExpenses',
        //     params: {
        //       projectId: project.value.id,
        //     },
        //   },
        //   icon: 'SendIcon',
        // },
        // {
        //   title: 'expenses',
        //   icon: 'SendIcon',
        //   children: [
        //     {
        //       title: 'all',
        //       route: {
        //         name: 'allExpenses',
        //         params: {
        //           projectId: project.value.id,
        //         },
        //       },
        //     },
        //     {
        //       title: 'byProduct',
        //       route: {
        //         name: 'expenses',
        //         params: {
        //           projectId: project.value.id,
        //         },
        //       },
        //     },
        //   ],
        // },
        upsell('budgets_access', {
          title: 'groupedExpenses',
          route: {
            name: 'groupedExpenses',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'PieChartIcon',
        }),
        {
          title: 'management',
          icon: 'BriefcaseIcon',
          children: compact([
            upsell('reports_access', {
              title: 'dashboard',
              route: {
                name: 'reports',
                params: {
                  projectId: project.value.id,
                },
              },
              icon: 'BarChart2Icon',
            }),
            upsell('income_tracking', {
              title: 'incomes',
              route: {
                name: 'incomes',
                params: {
                  projectId: project.value.id,
                },
              },
              icon: 'TrendingUpIcon',
            }),
            upsell('purchase_management', {
              title: 'expenses',
              route: {
                name: 'allExpenses',
                params: {
                  projectId: project.value.id,
                },
              },
              icon: 'TrendingDownIcon',
            }),
            upsell('budgets_access', {
              title: 'budget',
              route: {
                name: 'budget',
                params: {
                  projectId: project.value.id,
                },
              },
              icon: 'CalculatorIcon',
            }),
          ]),
        },
      ]
      if (!root.$helpers.validZone(project.value.address)) {
        const index = nav.findIndex(i => i.title === 'quotes')
        if (index > 0) {
          nav.splice(index, 1)
        }
      }
      return compact(nav)
    }
    return [
      {
        title: 'myProjects',
        route: {
          name: 'projects',
          params: {
            customerId: userData.value.scoped_roles[0]?.role_resource_id,
          },
        },
        icon: 'HomeIcon',
      },
    ]
  })

  const returnNavigationForUser = computed(() => {
    if (userData.value.role_name === 'admin') {
      if (isCustomerProjectRoute(route.value)) {
        return adminNavigationLikeCustomerProject.value
      }
      if (isCustomerAccountRoute(route.value, proxy.$route.params)) {
        return adminNavigationLikeCustomer.value
      }
      return adminNavigation.value
    }
    if (userData.value.role_name === 'customer') {
      if (
        ['projects', 'newProjects', 'editCustomer', 'members', 'new-member', 'allMyQuotes', 'allMyExpenses', 'accountsReceivable', 'WalletForCustomer', 'referrals', 'billing', 'new-billing', 'membership'].includes(
          route.value,
        )
      ) {
        return clientNavigation.value
      }
      return clientProjectNavigation.value
    }
    if (
      userData.value.role_name === 'supplier_admin'
      && supplier.value.approved_status === 'approved'
    ) {
      return supplierAdminNavigation.value
    }
    if (userData.value.role_name === 'auctioneer') {
      return auctioneerNavigation.value
    }
    return []
  })
  const mobileNavigation = computed(() => {
    if (project.value.id && project.value.id !== null) {
      return [
        {
          title: 'myProjects',
          route: {
            name: 'projects',
            params: {
              customerId: userData.value.scoped_roles[0]?.role_resource_id,
            },
          },
          icon: 'HomeIcon',
        },
        {
          title: 'quotes',
          route: {
            name: 'quotes',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'FileTextIcon',
        },
        {
          title: 'approvedQuotes',
          route: {
            name: 'approvedQuotes',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'CheckCircleIcon',
        },
        {
          title: 'groupedExpenses',
          route: {
            name: 'groupedExpenses',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'PieChartIcon',
        },
        {
          title: 'dashboard',
          route: {
            name: 'reports',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'BarChart2Icon',
        },
        {
          title: 'incomes',
          route: {
            name: 'incomes',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'TrendingUpIcon',
        },
        {
          title: 'expenses',
          route: {
            name: 'allExpenses',
            params: {
              projectId: project.value.id,
            },
          },
          icon: 'TrendingDownIcon',
        },
      ]
    }
    return [
      {
        title: 'myProjects',
        route: {
          name: 'projects',
          params: {
            customerId: userData.value.scoped_roles[0]?.role_resource_id,
          },
        },
        icon: 'HomeIcon',
      },
    ]
  })

  return {
    adminNavigation,
    adminNavigationLikeCustomer,
    adminNavigationLikeCustomerProject,
    supplierAdminNavigation,
    auctioneerNavigation,
    clientNavigation,
    clientProjectNavigation,
    returnNavigationForUser,
    mobileNavigation,
    route,
    project,
  }
}
